import { Search } from 'iconoir-react';
import { FC, useEffect, useRef, useState } from 'react';

import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { ResultSearchType } from '@savgroup-front-common/core/molecules/SearchWithDimmer/SearchWithDimmer.types';
import { EmptyState } from '@savgroup-front-common/primitive/components/empty-state';
import { RESULT_TYPE } from '@savgroup-front-common/types';
import { SearchResultList } from '@savgroup-front-common/types/SearchResultList';

import { ClearableSearchInputGroup } from '../../../ClearableSearchInputGroup/ClearableSearchInputGroup';
import { AppBarSearchResultSection } from '../AppBarSearchResultSection/AppBarSearchResultSection';

import messages from './messages';

interface AppBarSearchProps {
  query?: string;
  isLoading: boolean;
  isFetchingMore: boolean;
  focusedResultType?: RESULT_TYPE;
  results: SearchResultList<ResultSearchType>[];
  onSearch: (value: string) => void;
  onShowMore: (resultType: RESULT_TYPE) => void;
  shouldFocusOnMount?: boolean;
}

export const AppBarSearch: FC<AppBarSearchProps> = ({
  query = '',
  isLoading,
  isFetchingMore,
  focusedResultType,
  results,
  onSearch,
  onShowMore,
  shouldFocusOnMount,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const hasQuery = query.trim().length > 0;
  const visibleSections = results.filter((section) => !!section.hitCount);
  const hasResults = visibleSections.length > 0;
  const shouldShowPanel = isOpen && hasQuery && !isLoading;

  useEffect(() => {
    if (!shouldShowPanel) {
      return undefined;
    }

    const handlePointerDownOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDownOutside);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
    };
  }, [shouldShowPanel]);

  return (
    <div className="relative" ref={containerRef}>
      <ClearableSearchInputGroup
        className="h-8"
        value={query}
        onChange={(value) => {
          onSearch(value);
          setIsOpen(value.trim().length > 0);
        }}
        placeholder={safeFormattedIntlString(messages.searchPlaceholder)}
        clearAriaLabel={safeFormattedIntlString(messages.clearSearch)}
        isLoading={isLoading}
        autoFocus={shouldFocusOnMount}
        inputDataTestId="appBarSearchInput"
        clearDataTestId="appBarSearchClear"
        onFocus={() => {
          if (hasQuery) {
            setIsOpen(true);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setIsOpen(false);
          }
        }}
      />

      {shouldShowPanel && (
        <div
          data-testid="appBarSearchResults"
          className={
            'absolute top-full right-0 z-[60] mt-2 max-h-[70vh] w-[488px] ' +
            'overflow-y-auto rounded-md bg-surface-base-default p-2 stext-base-default ' +
            'shadow-md ring-1 ring-border-base-default outline-hidden animate-in fade-in-0 zoom-in-95'
          }
        >
          {!hasResults && (
            <EmptyState
              icon={<Search />}
              title={safeFormattedIntlString(messages.noResults)}
              description={safeFormattedIntlString(
                messages.noResultsDescription,
              )}
            />
          )}

          {hasResults && (
            <div className="flex flex-col gap-3">
              {visibleSections.map((section) => (
                <AppBarSearchResultSection
                  key={section.type}
                  section={section}
                  isSectionLoading={
                    isFetchingMore && focusedResultType === section.type
                  }
                  onShowMore={onShowMore}
                  onSelectResult={() => setIsOpen(false)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
